import type { DomainConfig } from '@core/types/domain-config.ts'
import type { DomainSettings } from '../types/domain-settings.ts'
import type { PromptGroup } from '../types/prompts.ts'
import type { SettingsStore } from '../storage/settings-storage-manager'
import type { RemoteResourceProvider } from './remote-resource-provider.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger'
import { Request } from '@core/request/request'
import { RequestMethod, type RequestOptions } from '@core/request/types'
import { Result } from '@core/models/result'
import { getCleanObject } from '@core/utils/get-clean-object'
import { Globals } from '../globals'
import { timeIntervalFromNow } from '@core/utils/time'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { TimestampedDataValue } from '@core/storage/abstract-data-store.ts'
import { ensureError } from '@core/utils/ensure-error.ts'

type CachedSettings = {
    value: DomainSettings
    /** Past its freshness window but still servable while a replacement is fetched. */
    isStale: boolean
}

/**
 * Number of network attempts for the merged document.
 *
 * Deliberately one. A 404 is already classified as not worth re-sending, so this is not what
 * bounds the retries - it makes the intent explicit at the call site, because this probe is
 * expected to fail for every domain until the producer has swept it and the legacy fallback
 * behind it is what provides resilience. Nothing here should wait on a retry budget to
 * discover that.
 */
const MERGED_DOCUMENT_MAX_ATTEMPTS = 1

/**
 * Loads the domain's configuration and prompts as a single document.
 *
 * These were previously two endpoints with separate caches on equal TTLs, costing two
 * requests per client per window to move a couple of kilobytes that seldom change — and
 * overwhelmingly returning `304`. This fetches them together, caches them as one entry under
 * one freshness window, and hands the two halves to their existing consumers through
 * {@link _configProvider} and {@link _promptsProvider}.
 *
 * Requests are collapsed three ways: one document instead of two, a longer freshness window,
 * and de-duplication of concurrent callers. The last matters because config is requested both
 * at init and on every e-commerce API call, and those are flushed concurrently once the SDK
 * finishes loading — previously each of them issued its own request.
 *
 * @internal
 */
export class DomainSettingsProvider {
    private readonly logger: Logger
    private cached: TimestampedDataValue<DomainSettings> | null = null

    /**
     * The in-flight hydration, shared by every caller until it settles. Without this, callers
     * arriving together past the freshness window each open their own request.
     */
    private hydrating: Promise<Result<DomainSettings, PNException>> | null = null

    /**
     * The document fetched for a preview session, held for the page load so preview costs one
     * request rather than one per caller. Never persisted, so preview cannot outlive the page.
     */
    private previewed: DomainSettings | null = null

    /** How long a stored document may be served for, counting the stale tail. */
    private static get _servableForMs(): number {
        return (Globals._pnDomainSettingsCacheTtlSeconds + Globals._pnDomainSettingsStaleWhileRevalidateSeconds) * 1000
    }

    constructor(
        public readonly domainKey: string,
        private readonly settingsStore: SettingsStore,
        logger?: Logger,
    ) {
        this.logger = logger ?? TaggedLogger._getInstance('DomainSettingsProvider')
    }

    async _get(): Promise<Result<DomainSettings, PNException>> {
        if (Globals._isPreviewModeEnabled) {
            // Fresh once per page load, not once per caller. `_hydrate` only collapses callers
            // that overlap, and this provider is consulted again on every e-commerce API call,
            // so without a ceiling a preview session would issue an uncached request per call -
            // and `?pn_preview=1` survives a shared or syndicated link, so that cost is not
            // confined to whoever opted in.
            if (this.previewed) {
                return Result.Ok(this.previewed)
            }

            this.logger.debug('Preview mode - ignoring stored settings')

            const res = await this._hydrate()
            if (res.isOk()) {
                this.previewed = res.unwrap()
            }

            return res
        }

        const cached = await this._getFromCache()
        if (!cached) {
            return this._hydrate()
        }

        if (cached.isStale) {
            // Not awaited: the point of the stale window is that expiry costs no page view
            // any latency. Failures are logged rather than surfaced because the caller is
            // being served a usable document regardless.
            this.logger.verbose('Serving stale settings while revalidating')
            void this._hydrate().catch((err) => this.logger.error(err))
        } else {
            this.logger.verbose('Hydrated from cache')
        }

        return Result.Ok(cached.value)
    }

    /** The domain config half, for consumers that only need configuration. */
    public get _configProvider(): RemoteResourceProvider<DomainConfig> {
        return {
            _get: async () => {
                const res = await this._get()
                return res.isErr() ? res : Result.Ok(res.unwrap().config)
            },
            _getCachedSync: () => this._getCachedSync()?.config ?? null,
        }
    }

    /** The prompts half, for consumers that only need prompts. */
    public get _promptsProvider(): RemoteResourceProvider<PromptGroup[]> {
        return {
            _get: async () => {
                const res = await this._get()
                return res.isErr() ? res : Result.Ok(res.unwrap().prompts)
            },
            _getCachedSync: () => this._getCachedSync()?.prompts ?? null,
        }
    }

    async _getFromCache(): Promise<CachedSettings | null> {
        const cached = await this.settingsStore._getDomainSettings()
        if (!cached) {
            return null
        }

        const ageMs = Math.abs(timeIntervalFromNow(cached.ts))
        const freshForMs = Globals._pnDomainSettingsCacheTtlSeconds * 1000

        if (ageMs < freshForMs) {
            this.logger.verbose(`${((freshForMs - ageMs) / 1000).toFixed(2)}s until next fetch from remote`)
            this.cached = cached
            return { value: cached.value, isStale: false }
        }

        if (ageMs < DomainSettingsProvider._servableForMs) {
            this.cached = cached
            return { value: cached.value, isStale: true }
        }

        this.cached = null
        return null
    }

    /**
     * The settings this provider would serve right now, without touching storage or the network.
     *
     * Judged against the same window `_get` uses, including the stale tail. Measuring against
     * the freshness window alone contradicted `_get`: a document `_getFromCache` had accepted
     * and was serving would be reported as absent here, and the read also cleared the memoised
     * copy, so the two views stayed inconsistent for the rest of the page load and
     * `_legacyContext.domain` degraded to an empty object.
     */
    public _getCachedSync(): DomainSettings | null {
        const injected = this._injectedSettings
        if (injected) {
            return injected
        }

        if (this.previewed) {
            return this.previewed
        }

        if (this.cached && Math.abs(timeIntervalFromNow(this.cached.ts)) < DomainSettingsProvider._servableForMs) {
            return this.cached.value
        }

        return null
    }

    /**
     * Config and prompts as injected into a domain's own SDK bundle, when both are present.
     *
     * Absent prompts are not treated as an empty set here: `_injectedPromptGroups` reports
     * `null` both when nothing was injected and when an empty array was, and resolving that
     * to "no prompts" would silently stop displaying them for any domain whose config reached
     * the SDK by some other route.
     */
    private get _injectedSettings(): DomainSettings | null {
        const config = Globals._injectedDomainConfig
        const prompts = Globals._injectedPromptGroups

        return config && prompts ? { config, prompts } : null
    }

    /**
     * Fetches, persists and returns the settings document, sharing one request between all
     * concurrent callers.
     */
    private _hydrate(): Promise<Result<DomainSettings, PNException>> {
        this.hydrating ??= this._hydrateFromRemote().finally(() => (this.hydrating = null))
        return this.hydrating
    }

    private async _hydrateFromRemote(): Promise<Result<DomainSettings, PNException>> {
        const settings = await this._resolveSettings()
        if (settings.isErr()) {
            return settings
        }

        const resolved = settings.unwrap()

        // Held in memory regardless of whether it persists: the document is valid for this page
        // load either way, and a storage failure should not force a second request.
        this.cached = { value: resolved, ts: Date.now() }

        // Persisted for the service worker's benefit as well as the SDK's, so a failure here is
        // logged rather than failing the load - the caller has a usable document already.
        try {
            await this.settingsStore._setDomainSettings(resolved)
        } catch (err) {
            this.logger.error(ensureError(err))
        }

        return Result.Ok(resolved)
    }

    private async _resolveSettings(): Promise<Result<DomainSettings, PNException>> {
        const injectedConfig = Globals._injectedDomainConfig

        if (injectedConfig) {
            // The config arrived with the domain's own bundle, so it is never worth a request.
            this.logger.debug('Using injected domain settings')

            const injectedPrompts = Globals._injectedPromptGroups
            const prompts = injectedPrompts ?? (await this._fetchPrompts()).unwrapOr([])

            return Result.Ok({ config: injectedConfig, prompts })
        }

        const merged = await this._fetchMergedDocument()
        if (merged.isOk()) {
            return merged
        }

        this.logger.verbose('Merged settings document unavailable - falling back to separate documents')
        return this._fetchSeparateDocuments()
    }

    /**
     * Request options shared by every settings fetch.
     *
     * Preview has to reach past the HTTP cache as well as past the stored copy. The CDN
     * serves these documents with a long `max-age`, so skipping storage alone would still
     * be answered from the browser cache and a publisher checking an edit would see the old
     * document with no indication why.
     */
    private get _requestOptions(): RequestOptions {
        return Globals._isPreviewModeEnabled ? { cacheMode: 'reload' } : {}
    }

    private async _fetchMergedDocument(): Promise<Result<DomainSettings, PNException>> {
        const resourceUrl = `${Globals._cdnDomainSettingsUrl}/${this.domainKey}/web/settings`

        const req = new Request(RequestMethod.GET, resourceUrl, null, {
            ...this._requestOptions,
            maxAttempts: MERGED_DOCUMENT_MAX_ATTEMPTS,
        })
        const res = await req._perform<{ config?: DomainConfig; prompts?: PromptGroup[] }>()
        if (res.isErr()) {
            return res
        }

        const data = res.unwrap().data

        // Both halves are required to be present, not merely truthy. An absent `prompts` key is
        // indistinguishable from an empty one once defaulted, so a half-written document would
        // be accepted and cached as "this domain has no prompts" for the whole servable window,
        // with nothing to fall back to and nothing to indicate anything was wrong. Treating it
        // as a failed fetch sends it to the pre-merge endpoints instead.
        if (!data?.config || !('prompts' in data)) {
            return Result.Err(new PNException(PNExceptionCode.DOMAIN_CONFIGURATION_LOAD_FAILED))
        }

        this.logger.verbose('Hydrated from remote')

        return Result.Ok({
            config: getCleanObject(data.config),
            prompts: (data.prompts ?? []).map((group) => getCleanObject(group)),
        })
    }

    /**
     * Hydrates from the pre-merge endpoints, for a domain whose merged document has not been
     * published yet. Prompts failing is not fatal here, matching the behaviour of the separate
     * providers this replaces: a domain still loads and simply displays nothing.
     */
    private async _fetchSeparateDocuments(): Promise<Result<DomainSettings, PNException>> {
        const [configRes, promptsRes] = await Promise.all([this._fetchConfig(), this._fetchPrompts()])

        if (configRes.isErr()) {
            return configRes
        }

        return Result.Ok({ config: configRes.unwrap(), prompts: promptsRes.unwrapOr([]) })
    }

    private async _fetchConfig(): Promise<Result<DomainConfig, PNException>> {
        const resourceUrl = `${Globals._cdnDomainSettingsUrl}/${this.domainKey}/web/config`

        const res = await new Request(
            RequestMethod.GET,
            resourceUrl,
            null,
            this._requestOptions,
        )._perform<DomainConfig>()
        if (res.isErr()) {
            return res
        }

        const data = res.unwrap().data
        if (!data) {
            return Result.Err(new PNException(PNExceptionCode.DOMAIN_CONFIGURATION_LOAD_FAILED))
        }

        return Result.Ok(getCleanObject(data))
    }

    private async _fetchPrompts(): Promise<Result<PromptGroup[], PNException>> {
        const resourceUrl = `${Globals._cdnDomainSettingsUrl}/${this.domainKey}/web/prompts`

        const res = await new Request(RequestMethod.GET, resourceUrl, null, this._requestOptions)._perform<
            PromptGroup[]
        >()
        if (res.isErr()) {
            return res
        }

        const data = res.unwrap().data
        if (!data) {
            return Result.Err(new PNException(PNExceptionCode.PROMPTS_LOAD_FAILED))
        }

        return Result.Ok(data.map((datum) => getCleanObject(datum)))
    }
}
